/**
 * İzleme kodları (Meta Pixel, Google Analytics 4) — çerez rızasına bağlı.
 *
 * Sunucu hiçbir izleme script'i basmaz; kimlikler `shared.tracking` prop'uyla gelir (yalnızca production'da,
 * bkz. Public::SharedData). Script'ler ziyaretçi ilgili kategoriye izin verdiği an yüklenir:
 *   GA4 → istatistik çerezleri, Meta Pixel → pazarlama çerezleri.
 * Yüklenen kod o anki sayfanın görüntülemesini kendisi sayar; Inertia gezintilerinde <head> yenilenmediği
 * için sonraki görüntülemeleri trackPageView gönderir. Kod yüklenmemişse global'ler yoktur, hiçbir şey olmaz.
 */
import type { ConsentChoice } from './consent'

declare global {
  interface Window {
    fbq?: FbqFunction
    _fbq?: FbqFunction
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  push: FbqFunction
  loaded: boolean
  version: string
}

export type TrackingIds = { meta_pixel_id: string | null; ga4_measurement_id: string | null }

let lastUrl: string | null = null

function currentUrl(): string {
  return window.location.pathname + window.location.search
}

// İlk sayfanın adresi: yüklenen kod onu zaten sayar, ilk `navigate` olayında tekrar sayılmasın.
export function initTracking(): void {
  if (typeof window === 'undefined') return
  lastUrl = currentUrl()
}

// Aynı adrese yeniden yükleme (router.reload, kısmi yükleme) sayfa görüntülemesi değildir.
export function trackPageView(): void {
  if (typeof window === 'undefined') return
  const url = currentUrl()
  if (url === lastUrl) return
  lastUrl = url

  window.fbq?.('track', 'PageView')
  window.gtag?.('event', 'page_view', { page_location: window.location.href })
}

/** Rıza verilen kategorilerin kodlarını yükler; zaten yüklüyse dokunmaz. */
export function applyTrackingConsent(choice: ConsentChoice, ids: TrackingIds): void {
  if (typeof window === 'undefined') return
  if (choice.analytics && ids.ga4_measurement_id) loadGoogleAnalytics(ids.ga4_measurement_id)
  if (choice.marketing && ids.meta_pixel_id) loadMetaPixel(ids.meta_pixel_id)
}

/** Yüklenmiş bir kodun kategorisinden rıza geri çekildi mi? (O zaman sayfa yenilenmeli: kod bellekten atılamaz.) */
export function trackingLoadedWithout(choice: ConsentChoice): boolean {
  if (typeof window === 'undefined') return false
  return (!choice.analytics && Boolean(window.gtag)) || (!choice.marketing && Boolean(window.fbq))
}

function loadScript(src: string): void {
  const script = document.createElement('script')
  script.async = true
  script.src = src
  document.head.appendChild(script)
}

// Google'ın gtag.js snippet'inin karşılığı (satır içi script yok, CSP nonce gerekmez).
function loadGoogleAnalytics(measurementId: string): void {
  if (window.gtag) return

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // gtag.js `arguments` nesnesinin kendisini bekler (dizi değil)
    window.dataLayer?.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId)
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`)
}

// Meta'nın fbevents.js snippet'inin karşılığı: script inene kadar çağrılar kuyrukta bekler.
function loadMetaPixel(pixelId: string): void {
  if (window.fbq) return

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args)
    else fbq.queue.push(args)
  } as FbqFunction
  fbq.queue = []
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  window.fbq = fbq
  window._fbq = fbq

  loadScript('https://connect.facebook.net/en_US/fbevents.js')
  fbq('init', pixelId)
  fbq('track', 'PageView')
}
